import React, { useState, useEffect, useRef } from 'react';
import { Input } from './input';
import { ChevronDown, Check, Search } from 'lucide-react';

// Country codes data with flags and validation patterns
const COUNTRIES = [
  { code: 'IN', name: 'India', flag: '🇮🇳', dialCode: '+91', format: '##### #####', pattern: /^[0-9]{10}$/, keywords: ['india', 'indian', 'bharat', 'hindustan'] },
  { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1', format: '(###) ###-####', pattern: /^[0-9]{10}$/, keywords: ['usa', 'america', 'united states', 'american'] },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44', format: '#### ### ####', pattern: /^[0-9]{10,11}$/, keywords: ['uk', 'britain', 'england', 'british', 'united kingdom'] },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1', format: '(###) ###-####', pattern: /^[0-9]{10}$/, keywords: ['canada', 'canadian'] },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', dialCode: '+61', format: '### ### ###', pattern: /^[0-9]{9}$/, keywords: ['australia', 'aussie', 'australian'] },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', dialCode: '+49', format: '### ########', pattern: /^[0-9]{10,11}$/, keywords: ['germany', 'german', 'deutschland'] },
  { code: 'FR', name: 'France', flag: '🇫🇷', dialCode: '+33', format: '# ## ## ## ##', pattern: /^[0-9]{9}$/, keywords: ['france', 'french'] },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', dialCode: '+39', format: '### ### ####', pattern: /^[0-9]{10}$/, keywords: ['italy', 'italian'] },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', dialCode: '+34', format: '### ### ###', pattern: /^[0-9]{9}$/, keywords: ['spain', 'spanish'] },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', dialCode: '+55', format: '(##) #####-####', pattern: /^[0-9]{10,11}$/, keywords: ['brazil', 'brazilian'] },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽', dialCode: '+52', format: '### ### ####', pattern: /^[0-9]{10}$/, keywords: ['mexico', 'mexican'] },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷', dialCode: '+54', format: '### ### ####', pattern: /^[0-9]{10}$/, keywords: ['argentina', 'argentinian'] },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', dialCode: '+81', format: '###-####-####', pattern: /^[0-9]{10,11}$/, keywords: ['japan', 'japanese'] },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', dialCode: '+82', format: '###-####-####', pattern: /^[0-9]{10,11}$/, keywords: ['korea', 'korean', 'south korea'] },
  { code: 'CN', name: 'China', flag: '🇨🇳', dialCode: '+86', format: '### #### ####', pattern: /^[0-9]{11}$/, keywords: ['china', 'chinese'] },
  { code: 'RU', name: 'Russia', flag: '🇷🇺', dialCode: '+7', format: '### ###-##-##', pattern: /^[0-9]{10}$/, keywords: ['russia', 'russian'] },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷', dialCode: '+90', format: '### ### ## ##', pattern: /^[0-9]{10}$/, keywords: ['turkey', 'turkish'] },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '+966', format: '### ### ####', pattern: /^[0-9]{9}$/, keywords: ['saudi', 'saudi arabia', 'ksa'] },
  { code: 'AE', name: 'UAE', flag: '🇦🇪', dialCode: '+971', format: '### ### ####', pattern: /^[0-9]{9}$/, keywords: ['uae', 'emirates', 'dubai', 'abu dhabi'] },
  { code: 'EG', name: 'Egypt', flag: '��🇬', dialCode: '+20', format: '### ### ####', pattern: /^[0-9]{10}$/, keywords: ['egypt', 'egyptian'] },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦', dialCode: '+27', format: '### ### ####', pattern: /^[0-9]{9}$/, keywords: ['south africa', 'south african'] },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬', dialCode: '+234', format: '### ### ####', pattern: /^[0-9]{10}$/, keywords: ['nigeria', 'nigerian'] },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪', dialCode: '+254', format: '### ### ####', pattern: /^[0-9]{9}$/, keywords: ['kenya', 'kenyan'] },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭', dialCode: '+63', format: '### ### ####', pattern: /^[0-9]{10}$/, keywords: ['philippines', 'filipino', 'philippine'] },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭', dialCode: '+66', format: '### ### ####', pattern: /^[0-9]{9}$/, keywords: ['thailand', 'thai'] },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳', dialCode: '+84', format: '### ### ####', pattern: /^[0-9]{9,10}$/, keywords: ['vietnam', 'vietnamese'] },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩', dialCode: '+62', format: '### #### ####', pattern: /^[0-9]{10,12}$/, keywords: ['indonesia', 'indonesian'] },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾', dialCode: '+60', format: '###-### ####', pattern: /^[0-9]{9,10}$/, keywords: ['malaysia', 'malaysian'] },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', dialCode: '+65', format: '#### ####', pattern: /^[0-9]{8}$/, keywords: ['singapore', 'singaporean'] },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', dialCode: '+64', format: '### ### ####', pattern: /^[0-9]{9,10}$/, keywords: ['new zealand', 'nz', 'kiwi'] },
];

interface InternationalPhoneInputProps {
  value: string;
  onChange: (value: string, country: any) => void;
  onValidation?: (isValid: boolean) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  defaultCountry?: string;
}

export const InternationalPhoneInput: React.FC<InternationalPhoneInputProps> = ({
  value,
  onChange,
  onValidation,
  className = '',
  placeholder = 'Enter phone number',
  disabled = false,
  error = false,
  defaultCountry = 'IN'
}) => {
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const found = COUNTRIES.find(c => c.code === defaultCountry);
    const country = found || COUNTRIES[0];
    // Ensure dialCode always starts with +
    if (!country.dialCode.startsWith('+')) {
      country.dialCode = '+' + country.dialCode;
    }
    return country;
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter countries based on search term (including keywords)
  const filteredCountries = COUNTRIES.filter(country => {
    if (!searchTerm.trim()) return true; // Show all countries when no search term
    const search = searchTerm.toLowerCase();
    return (
      country.name.toLowerCase().includes(search) ||
      country.dialCode.includes(searchTerm) ||
      country.code.toLowerCase().includes(search) ||
      country.keywords?.some(keyword => keyword.toLowerCase().includes(search))
    );
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    // Handle keyboard navigation
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isDropdownOpen) {
        if (event.key === 'Escape') {
          setIsDropdownOpen(false);
          setSearchTerm('');
          inputRef.current?.focus();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownOpen]);

  // Parse existing value to extract country and number
  useEffect(() => {
    if (value && value.startsWith('+')) {
      // Find matching country by dial code
      const country = COUNTRIES.find(c => value.startsWith(c.dialCode));
      if (country) {
        setSelectedCountry(country);
        setPhoneNumber(value.slice(country.dialCode.length).trim());
      }
    }
  }, [value]);

  // Format phone number based on country pattern
  const formatPhoneNumber = (number: string, format: string) => {
    const digits = number.replace(/\D/g, '');
    let formatted = '';
    let digitIndex = 0;

    for (let i = 0; i < format.length && digitIndex < digits.length; i++) {
      if (format[i] === '#') {
        formatted += digits[digitIndex];
        digitIndex++;
      } else {
        formatted += format[i];
      }
    }

    return formatted;
  };

  // Validate phone number
  const validatePhoneNumber = (number: string, country: any) => {
    const digits = number.replace(/\D/g, '');
    return country.pattern.test(digits);
  };

  // Handle phone number input change
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;
    
    // Remove all non-digit characters except for formatting
    const digits = input.replace(/\D/g, '');
    
    // Limit digits based on country max length
    const maxDigits = selectedCountry.format.split('#').length - 1;
    const limitedDigits = digits.slice(0, maxDigits);
    
    // Format the number
    const formatted = formatPhoneNumber(limitedDigits, selectedCountry.format);
    setPhoneNumber(formatted);
    
    // Create full international number
    const fullNumber = selectedCountry.dialCode + ' ' + limitedDigits;
    
    // Validate
    const isValid = validatePhoneNumber(limitedDigits, selectedCountry);
    onValidation?.(isValid);
    
    // Call onChange with full international number
    onChange(fullNumber, selectedCountry);
  };

  // Handle country selection
  const handleCountrySelect = (country: any) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchTerm('');
    
    // Update the full number with new country code
    const digits = phoneNumber.replace(/\D/g, '');
    const fullNumber = country.dialCode + ' ' + digits;
    onChange(fullNumber, country);
    
    // Focus back to input
    inputRef.current?.focus();
  };

  return (
    <div className={`international-phone-input ${className}`}>
      <div className={`phone-input-container ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}>
        {/* Country Selector */}
        <div className="country-selector" ref={dropdownRef}>
          <button
            type="button"
            className="country-button"
            onClick={() => {
              if (!disabled) {
                setIsDropdownOpen(!isDropdownOpen);
              }
            }}
            disabled={disabled}
            title={`${selectedCountry.name} ${selectedCountry.dialCode}`}
          >
            <span className="country-flag">{selectedCountry.flag}</span>
            <span className="country-code" role="text" aria-label={`Country code ${selectedCountry.dialCode}`}>
              <span className="plus-symbol">&#43;</span>
              <span className="dial-number">{selectedCountry.dialCode.replace('+', '')}</span>
            </span>
            <ChevronDown className={`chevron ${isDropdownOpen ? 'open' : ''}`} size={16} />
          </button>

          {/* Dropdown */}
          {isDropdownOpen && (
            <div className="country-dropdown">
              <div className="dropdown-search">
                <div className="search-input-wrapper">
                  <Search className="search-icon" />
                  <Input
                    type="text"
                    placeholder="Type to search countries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                    autoFocus
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="search-clear"
                      onClick={() => setSearchTerm('')}
                      aria-label="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="dropdown-list">
                {filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    className={`country-option ${selectedCountry.code === country.code ? 'selected' : ''}`}
                    onClick={() => handleCountrySelect(country)}
                  >
                    <span className="option-flag">{country.flag}</span>
                    <span className="option-name">{country.name}</span>
                    <span className="option-code">
                      <span className="plus-symbol">&#43;</span>
                      <span className="dial-number">{country.dialCode.replace('+', '')}</span>
                    </span>
                    {selectedCountry.code === country.code && (
                      <Check className="check-icon" size={16} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <Input
          ref={inputRef}
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          className="phone-number-input"
          disabled={disabled}
        />
      </div>

      {/* Format Example */}
      <div className="format-example">
        <span className="example-text">
          Format: {selectedCountry.dialCode} {selectedCountry.format.replace(/#/g, '0')}
        </span>
      </div>
    </div>
  );
};

export default InternationalPhoneInput;
